import * as React from "react";
import {
  useController,
  FieldValues,
  UseControllerProps,
} from "react-hook-form";
import DatePicker from "react-datepicker";
import { useMergeRefs } from "@chakra-ui/react";
import { CalendarInput, CalendarInputProps, MaskedCalendarInput } from "shared";
import { useDivideControlProps } from "./hooks";
import { isValid } from "date-fns";

const omittedProps = ["selected", "name", "onChange"] as const;

type FormCalendarInputProps<TFieldValues extends FieldValues> =
  UseControllerProps<TFieldValues> &
    Omit<CalendarInputProps, typeof omittedProps[number]> & {
      onChange?: CalendarInputProps["onChange"];
      masked?: boolean;
    };

function FormCalendarInputInner<TFieldValues extends FieldValues>(
  props: FormCalendarInputProps<TFieldValues>,
  ref?: React.ForwardedRef<DatePicker>
) {
  const { onChange: onChangeProp, disabled, masked, ...restProps } = props;
  const [calendarProps, controllerProps] = useDivideControlProps<
    TFieldValues,
    FormCalendarInputProps<TFieldValues>
  >(restProps);

  const {
    field: { ref: fieldRef, value, onChange, ...restField },
  } = useController(controllerProps);

  const calendarInputRef = useMergeRefs(ref, fieldRef);

  const getEventTargetValue = (evt: unknown): string | undefined => {
    if (!evt || typeof evt !== "object") return undefined;
    if (!("target" in evt)) return undefined;

    const target = (evt as { target?: unknown }).target;
    if (!target || typeof target !== "object") return undefined;
    if (!("value" in target)) return undefined;

    const val = (target as { value?: unknown }).value;
    return typeof val === "string" ? val : undefined;
  };

  const handleChange: CalendarInputProps["onChange"] = (date, event) => {
    const targetValue = getEventTargetValue(event);

    // When the user clears the input via keyboard, react-datepicker may emit a value
    // that later gets coerced into epoch (new Date(null) => 12-31-1969 for negative TZ).
    // Force `null` into RHF in that case.
    if (targetValue === "") {
      onChangeProp?.(null, event);
      onChange(null);
      return;
    }

    onChangeProp?.(date, event);
    onChange(date);
  };

  const date = value && isValid(new Date(value)) ? new Date(value) : null;

  return (
    <CalendarInput
      ref={calendarInputRef}
      {...calendarProps}
      customInput={masked ? <MaskedCalendarInput /> : calendarProps.customInput}
      {...restField}
      selected={date}
      onChange={handleChange}
      disabled={disabled}
    />
  );
}

const FormCalendarInput = React.forwardRef(FormCalendarInputInner) as <
  TFieldValues extends FieldValues
>(
  props: FormCalendarInputProps<TFieldValues> & {
    ref?: React.ForwardedRef<DatePicker>;
  }
) => ReturnType<typeof FormCalendarInputInner>;

export { FormCalendarInput };
export type { FormCalendarInputProps };
